import React, { useEffect, useState } from 'react';
import List from '@mui/material/List';
import { NavLink, useLocation } from 'react-router-dom';
import { AppBar } from 'src/components/Layout/NavbarStyles';
import SidebarData from 'src/components/SidebarData';
import {
  Drawer,
  StyledListItemButton,
  StyledListItemIcon,
  StyledTypography,
} from 'src/components/Layout/SidebarStyles';
import Tooltip from '@mui/material/Tooltip';
import 'src/style/sidebar.css';
import Navbar from 'src/components/Navbar';
import { styled } from '@mui/system';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import { useSelector } from 'react-redux';
import { SidebarGroupPopover } from 'src/components/Layout/SidebarGroupPopover';
import { ReactComponent as SidebarGroupIcon } from 'src/Images/icons/common/sidebarGroupIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { ReactComponent as ExtraIcon } from 'src/Images/icons/sidebar/extra-sidebar-icon.svg';
import { ReactComponent as Close } from 'src/Images/icons/common/close.svg';

const OverlappingDrawer = styled(Drawer)(() => ({
  zIndex: 100,
}));

const OverlappingAppBar = styled(AppBar)(() => ({
  zIndex: 200,
}));

function isSidebarIconActive(activePathUrlPatterns, currentPath) {
  if (!Array.isArray(activePathUrlPatterns)) return false;

  return activePathUrlPatterns.some((urlPattern) => {
    if (!urlPattern || typeof urlPattern !== 'string') return false;

    if (urlPattern.includes(':')) {
      const basePattern = urlPattern.split('/:')[0];
      return currentPath.startsWith(basePattern);
    }
    return currentPath === urlPattern;
  });
}

function Sidebar() {
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const location = useLocation();
  const currentPath = location.pathname;

  const filteredSidebarData = SidebarData();

  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [groupPopoverAnchorEl, setGroupPopoverAnchorEl] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleGroupPopoverOpen = (event, key) => {
    setGroupPopoverAnchorEl(event.currentTarget);
    setOpenGroupKey(key);
  };

  const handleGroupPopoverClose = () => {
    setGroupPopoverAnchorEl(null);
    setOpenGroupKey(null);
  };

  const isGroupPopoverOpen = (key) => openGroupKey === key;
  const isGroupActive = (item) => {
    if (!item.groupChild) return false;

    return item.groupChild.some((child) =>
      isSidebarIconActive(child.matchUrls, currentPath),
    );
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        toggleDrawer();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;

      if (
        isDrawerOpen &&
        !clickedElement.closest('.main-sidebar-width') &&
        !clickedElement.closest('.extended-sidebar')
      ) {
        setIsDrawerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDrawerOpen]);

  return (
    <>
      <div className={`theme-background-${themeColor}`}>
        <OverlappingAppBar position="fixed">
          <Navbar />
        </OverlappingAppBar>
      </div>

      <OverlappingDrawer variant="permanent" open={isDrawerOpen}>
        <div className="div-flex-row h-100">
          <div
            className={`div-flex-column div-space-between ${isDrawerOpen ? 'border-right' : ' '} main-sidebar-width`}
          >
            <List className="listStyle">
              {filteredSidebarData
                .filter((item) => !item.showInSecondarySidebar)
                .map((item) => {
                  const hasGroup = Array.isArray(item.groupChild);
                  return (
                    <div key={item.id}>
                      <Tooltip placement="left">
                        <div
                          className={`sidebar-selection-button theme-sidebar-selection-button-${themeColor}`}
                        >
                          {!hasGroup ? (
                            <NavLink
                              to={item.url}
                              className={({ isActive: routeActive }) =>
                                routeActive ? 'active' : 'unactive'
                              }
                              onClick={() => setIsDrawerOpen(false)}
                            >
                              {({ isActive: routeActive }) => (
                                <StyledListItemButton className="sidebar-button sidebar-icon">
                                  <CustomToolTip
                                    title={item?.name}
                                    offset={11}
                                    placement="right"
                                  >
                                    <StyledListItemIcon>
                                      {routeActive
                                        ? item.activeIcon
                                        : item.inactiveIcon}
                                    </StyledListItemIcon>
                                  </CustomToolTip>
                                  <StyledTypography>
                                    {item.name}
                                  </StyledTypography>
                                </StyledListItemButton>
                              )}
                            </NavLink>
                          ) : (
                            <StyledListItemButton
                              className="sidebar-button sidebar-icon"
                              activecolor={
                                isGroupActive(item)
                                  ? getColorCode(themeColor)
                                  : null
                              }
                              role="button"
                              tabIndex={0}
                              onClick={(e) =>
                                handleGroupPopoverOpen(e, item.id)
                              }
                            >
                              <div className="div-flex-row div-align-center">
                                <StyledListItemIcon>
                                  {isGroupActive(item)
                                    ? item.activeIcon
                                    : item.inactiveIcon}
                                </StyledListItemIcon>

                                <StyledTypography className="sidebarText">
                                  {item.name}
                                </StyledTypography>

                                <div className="sidebar-group-icon">
                                  <SidebarGroupIcon
                                    className="icon-size-xs"
                                    color={
                                      isGroupActive(item)
                                        ? '#ffffff'
                                        : getColorCode(themeColor)
                                    }
                                  />
                                </div>
                              </div>
                            </StyledListItemButton>
                          )}
                        </div>
                      </Tooltip>
                      {hasGroup && (
                        <SidebarGroupPopover
                          open={isGroupPopoverOpen(item.id)}
                          anchorEl={groupPopoverAnchorEl}
                          onClose={handleGroupPopoverClose}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          onMouseLeave={handleGroupPopoverClose}
                        >
                          <div className="div-flex-column rg-5">
                            <div
                              className={`horizontal-padding-20 vertical-padding-5 theme-pill-container-${themeColor}`}
                            >
                              <span className="text-sm text-grey text-semi-bold">
                                {item.name}
                              </span>
                            </div>

                            <div className="div-flex-column rg-10 horizontal-padding-10 vertical-padding-5">
                              {item.groupChild.map((child) => {
                                const childActive = isSidebarIconActive(
                                  child.matchUrls,
                                  currentPath,
                                );

                                return (
                                  <NavLink
                                    to={child.url}
                                    key={child.id}
                                    className="navlink-reset"
                                    onClick={handleGroupPopoverClose}
                                  >
                                    <div
                                      className={`div-flex-row cg-10 div-align-center secondary-sidebar-padding ${
                                        childActive
                                          ? `theme-border-color-${themeColor}`
                                          : 'group-sidebar-icon-shadow'
                                      }`}
                                    >
                                      <div
                                        className={
                                          childActive
                                            ? `theme-background-${themeColor} child-selected-icon`
                                            : ''
                                        }
                                      >
                                        {childActive
                                          ? child.activeIcon
                                          : child.inactiveIcon}
                                      </div>

                                      <div className="div-flex-column">
                                        <span className="text-black text-sm text-semi-bold text-ellipsis">
                                          {child.name}
                                        </span>
                                      </div>
                                    </div>
                                  </NavLink>
                                );
                              })}
                            </div>
                          </div>
                        </SidebarGroupPopover>
                      )}
                    </div>
                  );
                })}
              <div
                role="button"
                tabIndex={0}
                className={`sidebar-selection-button theme-sidebar-selection-button-${themeColor} ${isDrawerOpen ? 'active' : 'unactive'}`}
                onClick={toggleDrawer}
              >
                <StyledListItemButton className="sidebar-button sidebar-icon">
                  <CustomToolTip title="Extra's" offset={7} placement="right">
                    <StyledListItemIcon>
                      <ExtraIcon />
                    </StyledListItemIcon>
                  </CustomToolTip>
                  <StyledTypography className="sidebarText">
                    Extra&apos;s
                  </StyledTypography>
                </StyledListItemButton>
              </div>
            </List>
          </div>
          <div className="extended-sidebar">
            <div className="secondary-sidebar-container-padding">
              <div className="div-flex-column rg-20">
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-md text-semi-bold">Extra&apos;s</span>
                  <Close
                    className="icon-size-sm cursor-pointer"
                    color="#ccc"
                    onClick={() => setIsDrawerOpen(false)}
                  />
                </div>
                <div className="border-bottom" />
                <div className="div-flex-column rg-10">
                  {filteredSidebarData
                    .filter((text) => text.showInSecondarySidebar)
                    .map((text) => {
                      const isActive = isSidebarIconActive(
                        text.matchUrls,
                        currentPath,
                      );

                      return (
                        <div
                          key={text.id}
                          className={`sidebar-selection-button theme-sidebar-selection-button-${themeColor}`}
                        >
                          {text.active !== false ? (
                            <NavLink
                              to={text?.url}
                              className={({ isActive: navLinkIsActive }) =>
                                navLinkIsActive || isActive
                                  ? 'active a-tag-color-black'
                                  : 'unactive a-tag-color-black'
                              }
                              onClick={() => setIsDrawerOpen(false)}
                            >
                              <div className="div-flex-row cg-10 div-align-center secondary-sidebar-padding sidebar-button sidebar-icon secondary-sidebar-icon-container secondary-sidebar-icon-shadow">
                                {isActive ? text.activeIcon : text.inactiveIcon}
                                <span className="div-flex-column text-sm text-semi-bold">
                                  {text?.name}
                                </span>
                              </div>
                            </NavLink>
                          ) : (
                            <StyledListItemButton className="sidebar-button sidebar-icon secondary-sidebar-icon-container secondary-sidebar-icon-shadow">
                              <StyledListItemIcon>
                                {isActive ? text.activeIcon : text.inactiveIcon}
                              </StyledListItemIcon>
                              <StyledTypography className="sidebarText">
                                {text?.name}
                              </StyledTypography>
                            </StyledListItemButton>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </OverlappingDrawer>
    </>
  );
}

export default Sidebar;
